import { PatientHealthDataTableResponse } from '../../api/generated/models';
import { parseLocalDateTime } from '../../utils/formatDate';

export type SleepStageType = 'awake' | 'rem' | 'core' | 'deep' | 'inBed' | 'asleepUnspecified';

export const SleepStage: Record<SleepStageType, { position: number, label: string, color: string }> = {
  awake: { position: 0, label: 'Awake', color: '#FE8A73FF' },
  rem: { position: 1, label: 'REM', color: '#3FB1E7FF' },
  core: { position: 2, label: 'Core', color: '#1B81FEFF' },
  deep: { position: 3, label: 'Deep', color: '#403EA7FF' },
  inBed: { position: 4, label: 'In Bed', color: '#FFDD00' },
  asleepUnspecified: { position: 5, label: 'Asleep', color: '#1B81FEFF' },
};

export type SleepSegment = {
  id: number,
  type: SleepStageType,
  from: Date,
  to: Date,
  oxygen: number,
};

const typeMapping: Record<string, SleepStageType> = {
  asleepREM: 'rem',
  asleepDeep: 'deep',
  asleepCore: 'core',
  awake: 'awake',
  inBed: 'inBed',
  asleepUnspecified: 'asleepUnspecified',
};

/**
 * Normalizes the sleep data into a list of segments.
 * @param patientData - The patient health data table response.
 * @returns The list of sleep segments.
 */
export function getSleepSegments(patientData?: Pick<PatientHealthDataTableResponse, 'rows'>): SleepSegment[] {
  if (!patientData?.rows || !Array.isArray(patientData.rows)) {
    return [];
  }

  let segmentId = 0;

  const res = patientData.rows
    .slice()
    .reverse()
    .map((entry): SleepSegment | null => {
      if (
        typeof entry !== 'object'
        || entry === null
        || !('sleep_type' in entry)
        || !('sleep_start_date_time' in entry)
        || !('sleep_end_date_time' in entry)
      ) {
        return null;
      }

      const { sleep_type, sleep_start_date_time, sleep_end_date_time } = entry as {
        sleep_type: string,
        sleep_start_date_time: string,
        sleep_end_date_time: string,
      };

      const mappedType = typeMapping[sleep_type];
      if (!mappedType) {
        return null;
      }

      return {
        id: segmentId++,
        type: mappedType,
        from: parseLocalDateTime(sleep_start_date_time),
        to: parseLocalDateTime(sleep_end_date_time),
        oxygen: (entry as any).spo2_avg_pct ?? 0,
      };
    })
    .filter((segment): segment is SleepSegment => segment !== null)
    .sort((a, b) => a.from.getTime() - b.from.getTime());

  if (res.length === 0) {
    return res;
  }

  const trimmed = res.slice(
    res[0].type === 'awake' ? 1 : 0,
    res[res.length - 1].type === 'awake' ? res.length - 1 : res.length,
  );

  if (trimmed.length === 0) {
    return trimmed;
  }

  const hasDeepStages = trimmed.some((s) => s.type === 'deep' || s.type === 'rem' || s.type === 'core');
  const hasAwake = trimmed.some((s) => s.type === 'awake');
  const hasUnspecified = trimmed.some((s) => s.type === 'asleepUnspecified');

  if (!hasDeepStages && hasAwake && hasUnspecified) {
    return trimmed.filter((s) => s.type === 'awake' || s.type === 'asleepUnspecified');
  }

  return trimmed;
}

type ChartStage = { type: SleepStageType, row: number, label: string, color: string };
type ChartBar = {
  id: number,
  type: SleepStageType,
  x: number,
  y: number,
  width: number,
  height: number,
  color: string,
};
type ChartConnector = { x: number, y1: number, y2: number };
type ChartCol = { x: number, label: string };

export type SleepChartModel = {
  width: number,
  height: number,
  borderWidth: number,
  startHours: number,
  endHours: number,
  startMinutes: number,
  chartWidthMinutes: number,
  stageCount: number,
  rowHeight: number,
  barHeight: number,
  barTopOffset: number,
  stages: ChartStage[],
  cols: ChartCol[],
  bars: ChartBar[],
  connectors: ChartConnector[],
};

const toHourCount = (d: Date) => Math.floor(d.getTime() / (1000 * 60 * 60));
const toMinuteCount = (d: Date) => d.getTime() / (1000 * 60);

export function buildSleepChartModel(opts: {
  width: number,
  height: number,
  data?: Pick<PatientHealthDataTableResponse, 'rows'>,
}): SleepChartModel {
  const borderWidth = 2;
  const margin = 12;

  const segmentsRaw = getSleepSegments(opts.data);
  const sleepData = segmentsRaw.filter((s) => s.type !== 'inBed');

  const defaultOrder = (Object.keys(SleepStage) as SleepStageType[]).filter((t) => t !== 'inBed');
  const visibleTypes = sleepData.length
    ? Array.from(new Set(sleepData.map((s) => s.type))).sort((a, b) => SleepStage[a].position - SleepStage[b].position)
    : defaultOrder;

  const stages = visibleTypes.map((type, row) => ({
    type,
    row,
    label: SleepStage[type].label,
    color: SleepStage[type].color,
  }));
  const stageCount = stages.length;

  const rowHeight = stageCount ? (opts.height - margin) / stageCount : 0;
  const barHeight = rowHeight * 0.4;
  const barTopOffset = barHeight * 0.8;

  const startHours = sleepData.length
    ? toHourCount(sleepData[0].from)
    : Math.max(0, new Date().getHours() - 8);
  const endHours = sleepData.length
    ? toHourCount(sleepData[sleepData.length - 1].to)
    : Math.min(23, new Date().getHours() + 1);

  const colCount = Math.max(1, endHours - startHours + 1);
  const chartWidthMinutes = colCount * 60;

  const startMinutes = sleepData.length ? toHourCount(sleepData[0].from) * 60 : startHours * 60;

  const colWidth = opts.width / colCount;
  const cols: ChartCol[] = new Array(colCount).fill(null).map((_, index) => {
    const labelHour = sleepData.length ? (sleepData[0].from.getHours() + index) % 24 : -1;
    return {
      x: index * colWidth,
      label: labelHour >= 0 ? String(labelHour) : '',
    };
  });

  const stagesByType = new Map<SleepStageType, number>();
  stages.forEach((s) => stagesByType.set(s.type, s.row));

  const bars: ChartBar[] = sleepData.map((segment) => {
    const fromMinutes = toMinuteCount(segment.from) - startMinutes;
    const toMinutes = toMinuteCount(segment.to) - startMinutes;
    const x = (fromMinutes / chartWidthMinutes) * opts.width;
    const width = ((toMinutes - fromMinutes) / chartWidthMinutes) * opts.width;
    const row = stagesByType.get(segment.type) ?? SleepStage[segment.type].position;
    const y = row * rowHeight + barTopOffset + borderWidth;

    return {
      id: segment.id,
      type: segment.type,
      x,
      y,
      width,
      height: Math.max(0, barHeight - borderWidth * 2),
      color: SleepStage[segment.type].color,
    };
  });

  const connectors: ChartConnector[] = [];
  for (let i = 0; i < sleepData.length - 1; i++) {
    const curr = sleepData[i];
    const next = sleepData[i + 1];
    if (curr.type === next.type) {
      continue;
    }

    const currBar = bars[i];
    const nextBar = bars[i + 1];
    const x = currBar.x + currBar.width;
    const y1 = currBar.y + currBar.height / 2;
    const y2 = nextBar.y + nextBar.height / 2;
    connectors.push({ x, y1, y2 });
  }

  return {
    width: opts.width,
    height: opts.height,
    borderWidth,
    startHours,
    endHours,
    startMinutes,
    chartWidthMinutes,
    stageCount,
    rowHeight,
    barHeight,
    barTopOffset,
    stages,
    cols,
    bars,
    connectors,
  };
}
